import { ref } from "vue";
import { useApi, useToaster, type Urgency } from "@shopexperts/shared";

export interface SubmitExpertLeadInvitePayload {
  directThreadId?: string;
  email: string;
  firstName: string;
  lastName: string;
  brandName?: string;
  storeUrl: string;
  projectTitle: string;
  projectBrief?: string;
  urgency: Urgency;
  timezone?: string;
  selectedPackagedServiceId?: number;
  projectValueRangeId?: number;
}

export interface SubmitExpertLeadInviteResult {
  success: boolean;
}

export function useSubmitExpertLeadInvite(options: { showToast?: boolean } = {}) {
  const { showToast = true } = options;
  const api = useApi();
  const toaster = useToaster();

  const isSubmitting = ref(false);

  async function submitLeadInvite(
    payload: SubmitExpertLeadInvitePayload,
  ): Promise<SubmitExpertLeadInviteResult> {
    isSubmitting.value = true;

    try {
      await api.expert.leadsV2.submitLead({
        body: {
          ...(payload.directThreadId ? { direct_thread_id: payload.directThreadId } : {}),
          email: payload.email,
          first_name: payload.firstName,
          last_name: payload.lastName,
          brand_name: payload.brandName,
          store_url: payload.storeUrl,
          project_title: payload.projectTitle,
          project_brief: payload.projectBrief,
          urgency: payload.urgency,
          timezone: payload.timezone,
          selected_packaged_service_id: payload.selectedPackagedServiceId,
          project_value_range_id: payload.projectValueRangeId,
        },
      });

      if (showToast) {
        toaster.create({
          title: "Project created",
          description:
            "The project has been created successfully and the client has been notified.",
          type: "success",
        });
      }

      return { success: true };
    } catch (error: any) {
      console.error("[useSubmitExpertLeadInvite] Submit error:", error);

      if (showToast) {
        toaster.create({
          title: "Project invite failed",
          description:
            error?.response?.data?.message ||
            "Failed to create the project invite. Please try again.",
          type: "error",
        });
      }

      throw error;
    } finally {
      isSubmitting.value = false;
    }
  }

  return {
    submitLeadInvite,
    isSubmitting,
  };
}
